import { Auth } from '@aws-amplify/auth';
import { Hub } from '@aws-amplify/core';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';
import {
  WEB_SESSION_LAST_ACTIVITY_KEY,
  WEB_SESSION_STARTED_AT_KEY,
} from '../constants/WebSession';

const COGNITO_STORAGE_PREFIX = 'CognitoIdentityServiceProvider.';
const LEGACY_AUTH_STORAGE_KEYS = ['aws-amplify-federatedInfo'];

/* eslint-disable no-unused-vars */
type WebStorage = {
  length: number;
  key: (...args: number[]) => string | null;
  removeItem: (...args: string[]) => void;
  getItem: (...args: string[]) => string | null;
  setItem: (...args: string[]) => void;
};
/* eslint-enable no-unused-vars */

type BrowserWindow = {
  localStorage: WebStorage;
  sessionStorage: WebStorage;
};

const getBrowserWindow = () => (
  (globalThis as typeof globalThis & { window?: BrowserWindow }).window
);

const clearCognitoKeys = (storage: WebStorage) => {
  const cognitoKeys = Array.from({ length: storage.length }, (_, index) => storage.key(index))
    .filter((key): key is string => key != null
      && (key.startsWith(COGNITO_STORAGE_PREFIX) || LEGACY_AUTH_STORAGE_KEYS.includes(key)));

  cognitoKeys.forEach((key) => storage.removeItem(key));
};

export const clearPersistedAuthTokens = async () => {
  await AsyncStorage.removeItem('token').catch(() => undefined);

  if (Platform.OS !== 'web') return;

  const browserWindow = getBrowserWindow();
  if (browserWindow) clearCognitoKeys(browserWindow.localStorage);
};

export const clearWebAuthSessionTokens = () => {
  if (Platform.OS === 'web') {
    const browserWindow = getBrowserWindow();
    if (browserWindow) clearCognitoKeys(browserWindow.sessionStorage);
  }
};

export const clearAuthSession = async () => {
  await Auth.signOut().catch(() => undefined);
  await clearPersistedAuthTokens();
  clearWebAuthSessionTokens();

  const browserWindow = getBrowserWindow();
  if (browserWindow) {
    browserWindow.localStorage.removeItem(WEB_SESSION_LAST_ACTIVITY_KEY);
    browserWindow.localStorage.removeItem(WEB_SESSION_STARTED_AT_KEY);
  }

  Hub.dispatch('auth', { event: 'localSignOut' });
};

export const configureAuth = async (configuration: object) => {
  if (Platform.OS === 'web') {
    const browserWindow = getBrowserWindow();
    if (!browserWindow) throw new Error('Web session storage is unavailable');
    await clearPersistedAuthTokens();
    Auth.configure({ ...configuration, storage: browserWindow.sessionStorage });
    return;
  }

  Auth.configure(configuration);
};

export const getAuthToken = async () => {
  try {
    const session = await Auth.currentSession();
    return session.getIdToken().getJwtToken();
  } catch (error) {
    await clearAuthSession();
    throw error;
  }
};
